/*
 *  -----------------------------------------------------------------------
 *    <copyright file="filter.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { DropDownList } from '@progress/kendo-react-dropdowns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce, useTranslation } from '../../../hooks';
import TextField from './text-field';

import './filter.scss';

const TextCriteria = ({
    filterCriteria,
    fieldCriteria,
    setFilterCriteria,
    setFilterTextFocus,
    setFilterText,
    id,
    input,
    placeholder,
    filterText,
    'data-testid': dataTestId,
}: any) => {
    const { t } = useTranslation();

    return (
        <div className="text-criteria-component">
            {fieldCriteria && (
                <DropDownList
                    value={filterCriteria}
                    textField="text"
                    dataItemKey="id"
                    data={fieldCriteria}
                    onChange={(e: any) => {
                        setFilterCriteria(e.target.value);
                        setFilterTextFocus();
                    }}
                />
            )}
            <span className="k-textbox" style={{ width: '100%', marginLeft: filterCriteria ? 10 : 0 }}>
                <TextField
                    className="search-input"
                    id={id || 'search-text'}
                    placeholder={placeholder || t('enterSearchTerm')}
                    autoFocus
                    ref={input}
                    value={filterText}
                    onChange={(e: any) => setFilterText(e.target.value)}
                    variant="outlined"
                    label={t('filterSearch')}
                    slotProps={{
                        input: { labelWidth: 60 },
                    }}
                    data-testid={dataTestId}
                />
            </span>
        </div>
    );
};

interface FilterInterface {
    style?: any;
    id: string;
    onChange(data: any): any;
    fieldCriteria?: { id: string; text: string }[];
    placeholder?: string;
    'data-testid': string;
}

const Filter = ({ id, onChange, fieldCriteria, placeholder, 'data-testid': dataTestId }: FilterInterface) => {
    const { t } = useTranslation();

    const [filterCriteria, setFilterCriteria] = useState(null);
    const [filterText, setFilterText] = useState('');

    const input = useRef(null);
    const debouncedInput = useDebounce(filterText, 300);
    const onChangeCallback = useCallback(onChange, []);

    useEffect(() => {
        if (debouncedInput || !filterText.length)
            onChangeCallback({
                criteria: filterCriteria ? filterCriteria.id : null,
                text: filterText.trim().toUpperCase(),
            });

        if (fieldCriteria) {
            setFilterCriteria(fieldCriteria.length ? fieldCriteria[0] : null);
        }
    }, [filterCriteria, filterText, debouncedInput, onChangeCallback]);

    const setFilterTextFocus = () => input && input.current && input.current._input.focus();

    const InputControl = () => (
        <TextCriteria
            filterText={filterText}
            placeholder={placeholder}
            filterCriteria={filterCriteria}
            fieldCriteria={fieldCriteria}
            setFilterText={setFilterText}
            setFilterCriteria={setFilterCriteria}
            setFilterTextFocus={setFilterTextFocus}
            id={id}
            input={input}
            data-testid={dataTestId ?? id}
        />
    );

    return !filterCriteria ? (
        <div className="selections">
            <InputControl />
        </div>
    ) : (
        <div className="core-filter">
            <div className="label">{t('filter')}</div>
            <div className="selections">
                <InputControl />
            </div>
        </div>
    );
};

export default Filter;
